import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { CropperComponent } from 'angular-cropperjs';
import {
  emptyPromoCrops,
  MiniMenuCropFrame,
  MiniMenuCropRect,
  MiniMenuPromoCrops
} from 'src/app/services/mini-menu.service';

/**
 * One image, three device crops — following HUNGERY's ImageConfigurationComponent verbatim.
 *
 * The mechanism that keeps the image inside the modal is canon's, not CSS: the source image is
 * downscaled to fit the available stage and the cropper container is sized to the *result*
 * (minContainerWidth/Height). Clipping with a fixed CSS height instead lets a tall photo bleed
 * past the modal. Cropper.js already reports crop data in source-image pixels even when its
 * canvas is visually scaled, which is also what MiniMenuCropRect stores.
 *
 * Also canon: the image is fixed and the user moves/resizes the crop box (movable/zoomable off),
 * the frame tabs sit centred beneath the image, and anything projected into <ng-content> is
 * placed on the tab row, set apart from the frame choices.
 */
@Component({
  selector: 'app-mini-menu-crop-editor',
  templateUrl: './mini-menu-crop-editor.component.html',
  styleUrls: ['./mini-menu-crop-editor.component.scss']
})
export class MiniMenuCropEditorComponent implements OnChanges {
  @ViewChildren('frameCropper') cropperRefs!: QueryList<CropperComponent>;

  @Input() imageSource: string | null = null;
  @Input() frames: MiniMenuCropFrame[] = [];
  @Input() crops: MiniMenuPromoCrops = emptyPromoCrops();
  /** Cap for the crop stage. Canon uses 60% of the window height; that is the default. */
  @Input() stageMaxHeight: number | null = null;

  @Output() cropsChange = new EventEmitter<MiniMenuPromoCrops>();

  isLoading = false;
  loadFailed = false;
  @Output() readyChange = new EventEmitter<boolean>();
  @Output() loadError = new EventEmitter<void>();
  private imageLoadSequence = 0;
  activeFrame = '';
  cropperConfigs: Record<string, any> = {};
  /** Held false while the image is measured so the cropper mounts against final dimensions. */
  isStageReady = false;
  imageWidth = 0;
  imageHeight = 0;
  sourceImageWidth = 0;
  sourceImageHeight = 0;

  /**
   * Vertical room the editor's own chrome needs inside the stage budget: 30px above the image,
   * 18px below it, the frame-tab row, and canon's 28px beneath that. Reserving it here is what
   * keeps those gaps intact for a tall image — otherwise the image eats the space and the tabs
   * end up jammed against it.
   */
  private static readonly CHROME_HEIGHT = 110;

  private working: MiniMenuPromoCrops = emptyPromoCrops();
  private liveCrop: Record<string, MiniMenuCropRect> = {};

  constructor(private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['frames'] && this.frames.length && !this.frames.some((f) => f.key === this.activeFrame)) {
      this.activeFrame = this.frames[0].key;
    }
    if (changes['crops']) {
      this.working = {
        mobile: this.crops?.mobile || null,
        tablet: this.crops?.tablet || null,
        tabletPortrait: this.crops?.tabletPortrait || null
      };
      this.liveCrop = {};
    }
    if (changes['imageSource'] || changes['frames'] || changes['stageMaxHeight']) {
      this.prepareStage();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.prepareStage();
  }

  onFrameCrop(frameKey: string, event: any): void {
    if (!event || !event.detail) {
      return;
    }
    // Cropper.js event data is already based on the original image's natural size. Scaling it
    // again makes the rect exceed the source image whenever the preview was reduced to fit the
    // modal. Clamp after rounding as protection against fractional boundary values.
    const x = Math.max(0, Math.min(Math.round(event.detail.x), Math.max(0, this.sourceImageWidth - 1)));
    const y = Math.max(0, Math.min(Math.round(event.detail.y), Math.max(0, this.sourceImageHeight - 1)));
    this.liveCrop[frameKey] = {
      x,
      y,
      width: Math.max(1, Math.min(Math.round(event.detail.width), this.sourceImageWidth - x)),
      height: Math.max(1, Math.min(Math.round(event.detail.height), this.sourceImageHeight - y))
    };
    this.captureCrop(frameKey);
    this.cropsChange.emit({ ...this.working });
  }

  // ngbNav mounts one cropper per active tab; restore the saved rect so re-entering a frame
  // keeps its crop instead of snapping back to the default centred box.
  onCropperReady(frameKey: keyof MiniMenuPromoCrops): void {
    const saved = this.working[frameKey];
    if (!saved) {
      this.readyChange.emit(true);
      return;
    }
    const sequence = this.imageLoadSequence;
    setTimeout(() => {
      if (sequence !== this.imageLoadSequence) return;
      const cropper = this.cropperRefs?.first;
      if (cropper && cropper.cropper) {
        // Both stored rects and Cropper.js setData use source-image pixels.
        cropper.cropper.setData({
          x: saved.x,
          y: saved.y,
          width: saved.width,
          height: saved.height
        } as any);
      }
      this.readyChange.emit(true);
    }, 0);
  }

  onTabChange(event: NgbNavChangeEvent): void {
    this.readyChange.emit(false);
    this.captureCrop(String(event.activeId));
    this.cropsChange.emit({ ...this.working });
  }

  private captureCrop(frameKey: string): void {
    const live = this.liveCrop[frameKey];
    if (live && frameKey in this.working) {
      this.working[frameKey as keyof MiniMenuPromoCrops] = live;
    }
  }

  /**
   * Canon's resizeImage: measure the space available, downscale the source to fit it, and record
   * the factor. Sizing the cropper container to the result is what stops the image bleeding.
   */
  private prepareStage(): void {
    const sequence = ++this.imageLoadSequence;
    this.loadFailed = false;
    this.isLoading = !!this.imageSource;
    this.isStageReady = false;
    this.readyChange.emit(false);
    const source = this.imageSource;
    if (!source) {
      this.isStageReady = false;
      this.imageWidth = 0;
      this.imageHeight = 0;
      this.sourceImageWidth = 0;
      this.sourceImageHeight = 0;
      return;
    }

    const img = new Image();
    const isRemote = /^https?:\/\//i.test(source) && source.indexOf(window.location.origin) !== 0;
    if (isRemote) {
      img.crossOrigin = 'anonymous';
    }

    const failLoad = () => {
      if (sequence !== this.imageLoadSequence) return;
      this.isLoading = false;
      this.isStageReady = false;
      this.loadFailed = true;
      this.loadError.emit();
    };

    img.onload = () => {
      if (sequence !== this.imageLoadSequence) return;
      let width = img.naturalWidth || img.width;
      let height = img.naturalHeight || img.height;
      if (!width || !height) {
        failLoad();
        return;
      }
      this.sourceImageWidth = width;
      this.sourceImageHeight = height;

      // Canon leaves a 50px side margin so the crop handles stay reachable; the vertical budget
      // additionally gives up CHROME_HEIGHT to the gaps and the tab row.
      const hostWidth = this.host.nativeElement.clientWidth || window.innerWidth * 0.8;
      const maxWidth = Math.max(160, hostWidth - 50);
      const budget = this.stageMaxHeight || window.innerHeight * 0.6;
      const maxHeight = Math.max(160, budget - MiniMenuCropEditorComponent.CHROME_HEIGHT);

      if (width > maxWidth || height > maxHeight) {
        const scale = Math.min(maxWidth / width, maxHeight / height);
        width = Math.max(1, Math.floor(width * scale));
        height = Math.max(1, Math.floor(height * scale));
      }

      this.imageWidth = width;
      this.imageHeight = height;
      this.buildCropperConfigs();

      // Remount so the cropper measures the container at its final size.
      this.isStageReady = false;
      setTimeout(() => {
        if (sequence !== this.imageLoadSequence) return;
        this.isLoading = false;
        this.isStageReady = true;
      }, 0);
    };
    img.onerror = failLoad;
    img.src = source;
  }

  /** Cropper options copied from ImageConfigurationComponent. */
  private buildCropperConfigs(): void {
    const configs: Record<string, any> = {};
    for (const frame of this.frames) {
      configs[frame.key] = {
        aspectRatio: frame.ratio,
        movable: false,
        zoomable: false,
        scalable: false,
        autoCropArea: 1,
        responsive: true,
        background: false,
        checkCrossOrigin: false,
        checkOrientation: false,
        viewMode: 1,
        minContainerHeight: this.imageHeight,
        minContainerWidth: this.imageWidth
      };
    }
    this.cropperConfigs = configs;
  }
}
